import React, { useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { Button, Card, Tag, Row, Col, Progress, Divider } from 'antd';
import { PlusOutlined, EyeOutlined, ArrowLeftOutlined } from '@ant-design/icons';
import { getApi } from 'redux/sagas/getApiDataSaga';
import { SERVER_IP } from 'assets/Config';
import Loader from 'components/loader';
import { MOCK_WORKOUT_PLANS } from 'data/workoutMockData';
import { useI18n } from 'hooks/useI18n';
import { translateWorkoutStatus } from 'helpers/workoutI18n';

const STATUS_COLORS = {
	active: 'processing',
	completed: 'success',
	paused: 'warning',
	cancelled: 'error',
};

const CustomerPlans = () => {
	const { customerId } = useParams();
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const { t } = useI18n();
	const globalRedux = useSelector((state) => state.globalRedux);
	const workoutPlanRedux = useSelector((state) => state.workoutPlanRedux);

	const fetchPlans = useCallback(() => {
		dispatch(getApi('GET_WORKOUT_PLANS', `${SERVER_IP}api/workoutPlan/customer/${customerId}`));
	}, [dispatch, customerId]);

	useEffect(() => {
		fetchPlans();
	}, [fetchPlans]);

	const allPlans = workoutPlanRedux?.customerPlans?.length
		? workoutPlanRedux.customerPlans
		: MOCK_WORKOUT_PLANS.filter((p) => p.customerId === customerId || customerId === 'cust1');

	const activePlan = allPlans.find((p) => p.status === 'Active');
	const historyPlans = allPlans.filter((p) => p.status !== 'Active');
	const loading = globalRedux.apiStatus.GET_WORKOUT_PLANS === 'PENDING';
	const customerName = activePlan?.customerName || t('workouts.plan.customer_list.customer_fallback');

	if (loading) return <Loader />;

	return (
		<Row>
			<Col span={24} style={{ padding: 20 }}>
				<Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
					<Col>
						<Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/customers')}>
							{t('common.back')}
						</Button>
					</Col>
					<Col>
						<strong style={{ fontSize: 16 }}>{t('workouts.plan.customer_list.title', { customerName })}</strong>
					</Col>
					<Col>
						<Button
							type="primary"
							icon={<PlusOutlined />}
							onClick={() => navigate(`/workout-plan/assign/${customerId}`)}
						>
							{t('workouts.plan.assign.assign_plan')}
						</Button>
					</Col>
				</Row>

			{/* Active plan */}
			{activePlan && (
				<>
					<h3 style={{ marginBottom: 8 }}>{t('workouts.plan.customer_list.active_plan')}</h3>
					<Card
						style={{ marginBottom: 16, borderLeft: '4px solid #52c41a' }}
						actions={[
							<Button
								key="view"
								type="link"
								icon={<EyeOutlined />}
								onClick={() => navigate(`/workout-plan/${activePlan.id}`)}
							>
									{t('workouts.plan.customer_list.view_details')}
							</Button>,
						]}
					>
						<Row gutter={16} align="middle">
							<Col flex="auto">
								<div style={{ fontWeight: 700, fontSize: 16, marginBottom: 4 }}>{activePlan.name}</div>
								<div style={{ color: '#666', marginBottom: 8 }}>
									{t('workouts.plan.customer_list.start_label')}: {activePlan.startDate} &nbsp;·&nbsp; {t('workouts.plan.customer_list.end_label')}: {activePlan.endDate}
								</div>
								<Progress
									percent={Math.round((activePlan.currentDay / activePlan.durationDays) * 100)}
									format={() => t('workouts.plan.customer_list.day_progress', { current: activePlan.currentDay, total: activePlan.durationDays })}
									strokeColor="#52c41a"
								/>
							</Col>
							<Col>
								<Tag color={STATUS_COLORS[activePlan.status]}>{translateWorkoutStatus(t, activePlan.status, activePlan.status)}</Tag>
							</Col>
						</Row>
					</Card>
				</>
			)}

			{!activePlan && (
				<Card style={{ marginBottom: 16, textAlign: 'center', color: '#999' }}>
					{t('workouts.plan.customer_list.no_active_plan')}
				</Card>
			)}

			{/* History */}
			{historyPlans.length > 0 && (
				<>
					<Divider orientation="left">{t('workouts.plan.customer_list.history')}</Divider>
					<Row gutter={[12, 12]}>
						{historyPlans.map((plan) => (
							<Col key={plan.id} xs={24} sm={12} xl={8}>
								<Card
									size="small"
									actions={[
										<Button
											key="view"
											type="link"
											icon={<EyeOutlined />}
											onClick={() => navigate(`/workout-plan/${plan.id}`)}
										>
											{t('common.view')}
										</Button>,
									]}
								>
									<div style={{ fontWeight: 600 }}>{plan.name}</div>
									<div style={{ color: '#888', fontSize: 12 }}>
										{plan.startDate} → {plan.endDate}
									</div>
									<Tag color={STATUS_COLORS[plan.status]} style={{ marginTop: 4 }}>
										{translateWorkoutStatus(t, plan.status, plan.status)}
									</Tag>
								</Card>
							</Col>
						))}
					</Row>
				</>
			)}
			</Col>
		</Row>
	);
};

export default CustomerPlans;
